import { Component, OnInit, Input } from '@angular/core';

import { User } from 'src/app/domains/users/models';
import { UsersService } from 'src/app/domains/users/services';

import { Company, ContactsConfiguration } from 'src/app/domains/companies/models';

import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddUserPopupComponent } from '../add-user-popup/add-user-popup.component';
import { EditUserPopupComponent } from '../edit-user-popup/edit-user-popup.component';
import { SetUserPasswordPopupComponent } from '../set-user-password-popup/set-user-password-popup.component';
import { take } from 'rxjs/operators';
import { LinksPopupComponent } from 'src/app/shared/components/links-popup/links-popup.component';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { CompanyService } from 'src/app/domains/companies/services';
import { forkJoin, of } from 'rxjs';
import { SubscriptionService } from 'src/app/domains/subscription/services';
import { catchError } from 'rxjs/operators';
import { Subscription } from 'src/app/domains/subscription/models';
@Component({
  selector: 'gdp-company-users',
  templateUrl: './company-users.component.html',
  styleUrls: ['./company-users.component.scss']
})
export class CompanyUsersComponent implements OnInit {
  @Input() company: Company;
  public users: User[];
  public loading: boolean = false;
  public loadingContactsConfig: boolean = false;
  public includeUsers: boolean;
  public requestFailed: boolean = false;
  public showSyncPasswordButton: boolean = false;
  subscription: Subscription;
  showToggle: boolean;

  constructor(
    private usersService: UsersService,
    private notification: NotificationService,
    private modalServce: NgbModal,
    private translate: TranslateService,
    private companyService: CompanyService,
    private subscriptionService: SubscriptionService) {}

  ngOnInit() {
    this.loadingContactsConfig = true;
    this.getUsers();
    forkJoin({
      configurations: this.companyService.getContactsConfiguration(this.company.companyId).pipe(catchError(() => { this.requestFailed = true; return of(undefined) })),
      subscription: this.subscriptionService.getSubscription(this.company.companyId).pipe(catchError(() => { return of(undefined) })),
    })
    .subscribe((res: {
      configurations: ContactsConfiguration,
      subscription: Subscription
    }) => {
      this.loadingContactsConfig = false;
      this.includeUsers = res.configurations?.include_users;
      this.showToggle = res.subscription?.addons.some(el => el.id.includes("address_book"));
      this.showSyncPasswordButton =  res.subscription?.addons.some(el => el.id.includes("chat_platform"));
    })
  }

  getUsers() {
    this.loading = true;
    this.usersService.getUsers(this.company.companyId).subscribe((res: any) => {
      this.users = res;
      this.loading = false;
    });
  }

  onDeleteUser(user: User) {
    this.notification.showConfirmation('company.users.confirm-delete').then(
      (result: string) => {
        if (result === 'ok') {
          user._isDeleting = true;
          this.usersService.deleteUser(this.company.companyId, user.userId)
            .subscribe(
              () => {
                this.users = this.users.filter((el) => el.userId !== user.userId);
                this.notification.success('company.users.user-deleted')
              },
              (err: HttpErrorResponse) => {
                const links = err.error?.body?.links;
                if (err.status === 409 && links != null) {
                  const modal = this.modalServce.open(LinksPopupComponent, { size: 'lg'});
                  modal.componentInstance.links = links;
                }
                user._isDeleting = false;
              }
            );
        }
      })
  }

  openSetUserPasswordPopup(u: User) {
    const modal = this.modalServce.open(
      SetUserPasswordPopupComponent,
      { size: 'md' }
    );
    modal.componentInstance.user = u;
    modal.componentInstance.companyId = this.company.companyId;

    modal.componentInstance.onSetUserPassword
      .pipe(take(1))
      .subscribe((message: string) => {
        this.getUsers();
        this.notification.success(message);
      });
  }

  openAddUserPopup() {
    const user: User = {
      userId: 0,
      firstName: null,
      lastName: null,
      phoneNumber: null,
      email: null,
      extension: null,
      _isEditing: false,
      enabled: false,
      isAdministrator: false,
      password: null
    }
    const modal = this.modalServce.open(
      AddUserPopupComponent,
      { size: 'md' }
    );
    modal.componentInstance.user = user;
    modal.componentInstance.companyId = this.company.companyId;

    modal.componentInstance.onAddUser.pipe(take(1)).subscribe(() => {
      this.getUsers();
      this.notification.success('company.user.user-added');
    });
  }

  openEditUserPopup(user: User) {
    user._isEditing = true;
    const modal = this.modalServce.open(EditUserPopupComponent, {
      size: 'md'
    });
    modal.componentInstance.user = user;
    modal.componentInstance.companyId = this.company.companyId;

    modal.componentInstance.onEditUser.pipe(take(1)).subscribe(() => {
      this.getUsers();
      this.notification.success('company.user.user-update');
    })
  }

  toggelContactsConfiguration() {
    this.loadingContactsConfig = true;
    const message = this.includeUsers ? 'company.users.include-users.confirmation.on-disable' : 'company.users.include-users.confirmation.on-enable';
     this.notification.showConfirmation(message).then((result: string) => {
      if(result === 'ok') {
        this.companyService.toggelContactsConfiguration(this.company.companyId, { include_users: !this.includeUsers }).subscribe((res: ContactsConfiguration) => {
         this.includeUsers = res.include_users;
         this.loadingContactsConfig = false;
        }, () => {
          this.loadingContactsConfig = false;
        });
      } else {
        this.loadingContactsConfig = false;
      }
     });
    }

    getContactsConfiguration() {
      this.loadingContactsConfig = true;
      this.companyService.getContactsConfiguration(this.company.companyId).subscribe((res: ContactsConfiguration) => {
        this.includeUsers = res.include_users;
        this.loadingContactsConfig = false;
        this.requestFailed = false;
      },
      () => {
        this.requestFailed = true;
        this.loadingContactsConfig = false;
      }
      )
    }

    onClearPresence(user: User) {
      this.notification.showConfirmation('company.users.confirm-presence-reset').then((result: string) => {
        if(result === 'ok') {
          this.loading = true;
          this.usersService.resetPresence(this.company.pbxId, user?.pbxId).subscribe(() => {
            this.loading = false;
            this.notification.success('company.users.presence-resetted');
          },
          () => {
            this.loading = false;
          })
        } else {
          this.loading = false;
        }
      })
    }

    public syncPasswords(): void {
      const confirmationMessage = 'company.users.sync-passwords.message';

      this.notification.showConfirmationWithAlphaname(confirmationMessage, this.company.alphaname)
        .then((result: string) => {
          if (result === 'ok') {
            this.loading = true;
            this.usersService.syncPasswords(this.company.companyId).subscribe({
              next: () => {
                this.loading = false;
                this.notification.success('company.users.sync-passwords.success');
              },
              error: () => {
                this.loading = false;
              }
            })
          }
      })
    }
  }
